<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const props = defineProps({
    canBuy: { type: Boolean, default: false },
    billingPlan: { type: String, default: null },
    hasStripeCustomer: { type: Boolean, default: false },
    hasCard: { type: Boolean, default: false },
});

const keyword = ref('');
const results = ref([]);
const isSearching = ref(false);
const isOpeningBilling = ref(false);
const lastError = ref(null);
const debounceTimer = ref(null);
const requestSeq = ref(0);
const isSandbox = ref(false);
const selectedTlds = ref([]);
const searchInputEl = ref(null);

const availableResults = computed(() => results.value.filter(r => r.purchasable));
const unavailableResults = computed(() => results.value.filter(r => !r.purchasable));
const hasSearched = computed(() => (keyword.value || '').trim().length >= 2);

const cannotBuyReason = computed(() => {
    if (props.canBuy) return null;
    if (!props.billingPlan) return _mft('setting:domains.buy.requiresPlan');
    if (!props.hasCard) return _mft('setting:domains.buy.requiresCard');
    return _mft('setting:domains.buy.unavailableForAccount');
});

const billingActionLabel = computed(() => {
    if (isOpeningBilling.value) return _mft('setting:domains.buy.openingStripe');

    return props.hasStripeCustomer ? _mft('setting:domains.buy.openStripeBilling') : _mft('setting:domains.buy.addCard');
});

const resultPriceLabel = (result) => {
    if (result.purchase_price_eur === null) {
        return _mft('setting:domains.buy.pricingPending');
    }

    return _mft('setting:domains.buy.price.firstYearThen', {
        purchasePrice: result.purchase_price_eur.toFixed(2),
        renewalPrice: result.renewal_price_eur?.toFixed(2) ?? '-',
    });
};

const onInput = () => {
    if (debounceTimer.value) clearTimeout(debounceTimer.value);
    debounceTimer.value = setTimeout(performSearch, 350);
};

const performSearch = async () => {
    const term = (keyword.value || '').trim();
    if (term.length < 2) {
        results.value = [];
        return;
    }

    const mySeq = ++requestSeq.value;
    isSearching.value = true;
    lastError.value = null;
    try {
        const { data } = await axios.post(route('settings.domains.buy.search'), {
            keyword: term,
            tld_filter: selectedTlds.value,
        });
        if (mySeq !== requestSeq.value) return;
        results.value = data.results || [];
        isSandbox.value = !!data.sandbox;
    } catch (e) {
        if (mySeq !== requestSeq.value) return;
        lastError.value = e?.response?.data?.message || _mft('setting:domains.buy.searchFailed');
    } finally {
        if (mySeq === requestSeq.value) isSearching.value = false;
    }
};

const buy = async (domain) => {
    try {
        const { data } = await axios.post(route('settings.domains.buy.quote'), {
            domain,
            years: 1,
        });
        window.location.href = route('settings.domains.buy.review', data.token);
    } catch (e) {
        lastError.value = e?.response?.data?.message || _mft('setting:domains.buy.quoteFailed');
    }
};

const openBillingAction = async () => {
    if (isOpeningBilling.value) return;

    isOpeningBilling.value = true;
    lastError.value = null;

    const returnUrl = new URL(
        route('settings.domains.buy.start'),
        window.location.origin
    ).href;

    try {
        const { data } = props.hasStripeCustomer
            ? await axios.post(route('billing.subscription.portal'), {
                return_url: returnUrl,
            })
            : await axios.post(route('billing.subscription.cardSetup'), {
                success_url: `${returnUrl}?checkout=setup-success&session_id={CHECKOUT_SESSION_ID}`,
                cancel_url: `${returnUrl}?checkout=setup-cancelled`,
            });

        if (data?.url) {
            window.open(data.url, '_self', 'noopener');
            return;
        }

        lastError.value = _mft('setting:domains.buy.openStripeFailed');
    } catch (e) {
        lastError.value = e?.response?.data?.error || _mft('setting:domains.buy.openStripeFailed');
    } finally {
        isOpeningBilling.value = false;
    }
};

const toggleTld = (tld) => {
    const idx = selectedTlds.value.indexOf(tld);
    if (idx === -1) selectedTlds.value.push(tld);
    else selectedTlds.value.splice(idx, 1);
    if (hasSearched.value) performSearch();
};

watch(keyword, onInput);

onMounted(() => {
    keyword.value = '';
    searchInputEl.value?.focus();
});

onBeforeUnmount(() => {
    if (debounceTimer.value) clearTimeout(debounceTimer.value);
});
</script>

<template>
    <div class="mx-auto w-full max-w-3xl space-y-6 p-6">
        <frosted-bar
            :breadcrumbs="[{url: route('settings.company.settings.domains'), label: _mft('setting:domains.allDomains')}]"
        >
            <h1>{{ _mft('setting:domains.buy.title') }}</h1>
        </frosted-bar>

        <div class="text-center">
            <h2>{{ _mft('setting:domains.buy.heading') }}</h2>
            <p class="text-sm text-gray-600">
                {{ _mft('setting:domains.buy.byline') }}
            </p>
        </div>

        <div v-if="cannotBuyReason" class="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            {{ cannotBuyReason }}
            <button
                v-if="!hasCard"
                type="button"
                class="mf-link ml-2 font-medium"
                :disabled="isOpeningBilling"
                @click="openBillingAction"
            >
                {{ billingActionLabel }}
            </button>
        </div>

        <div class="max-w-lg mx-auto">
            <label class="block text-sm font-medium text-gray-700 sr-only">{{ _mft('setting:domains.buy.findDomain') }}</label>
            <div class="relative">
                <i class="fa-regular fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                    ref="searchInputEl"
                    v-model="keyword"
                    type="text"
                    class="block w-full rounded-xl border border-gray-300 py-2 pl-9 pr-3 text-base focus:border-brandPrimary-400 focus:outline-none focus:ring-2 focus:ring-brandPrimary-100"
                    :placeholder="_mft('setting:domains.buy.placeholder')"
                    autocomplete="off"
                    spellcheck="false"
                />
                <span
                    class="absolute right-1 top-1/2 -translate-y-1/2"
                >
                    <button-base
                        :style-type="isSearching ? 'disabled' : 'primary'"
                        @click="performSearch"
                    >
                        {{ isSearching ? _mft('shared:searching') : _mft('shared:search') }}
                    </button-base>
                </span>
            </div>
        </div>

        <div v-if="lastError" class="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {{ lastError }}
        </div>

        <div
            v-if="isSandbox && results.length"
            class="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800"
        >
            <b>{{ _mft('setting:domains.buy.sandboxMode') }}:</b>
            {{ _mft('setting:domains.buy.sandboxModeDescriptionBefore') }}
            <code>NAMECOM_BASE_URL</code>
            {{ _mft('setting:domains.buy.sandboxModeDescriptionAfter') }}
        </div>

        <div v-if="isSearching && !results.length" class="space-y-2">
            <mf-spinner-medium class="w-24 h-12" />
        </div>

        <p v-if="availableResults.length" class="px-1 text-xs text-gray-500">
            {{ _mft('setting:domains.buy.review.vatNotice') }}
        </p>

        <transition-group
            v-if="availableResults.length"
            tag="ul"
            name="result"
            class="space-y-2"
        >
            <li
                v-for="r in availableResults"
                :key="r.domain"
            >
                <card-basic>
                    <div class="group flex items-center justify-between gap-4">
                        <div class="min-w-0 flex-1">
                            <div class="flex items-center gap-2">
                                <span class="text-base font-semibold text-gray-900 truncate">{{ r.domain }}</span>
                                <span
                                    v-if="r.premium"
                                    class="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800"
                                >{{ _mft('setting:domains.buy.premium') }}</span>
                            </div>
                            <div class="mt-0.5 text-xs text-gray-500">
                                {{ resultPriceLabel(r) }}
                            </div>
                        </div>
                        <button-base
                            :style-type="canBuy ? 'success' : 'disabled'"
                            :disabled="!canBuy || r.purchase_price_eur === null"
                            @click="canBuy ? buy(r.domain) : null"
                        >
                            {{ _mft('setting:domains.buy.buy') }}
                        </button-base>
                    </div>
                </card-basic>
            </li>
        </transition-group>

        <div v-if="hasSearched && unavailableResults.length">
            <h3 class="mb-2 px-1 text-xs font-medium uppercase tracking-wide text-gray-500">{{ _mft('setting:domains.buy.alreadyTaken') }}</h3>
            <ul class="space-y-2">
                <li
                    v-for="r in unavailableResults"
                    :key="r.domain"
                    class="flex items-center justify-between rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-3"
                >
                    <span class="text-sm font-medium text-gray-500 line-through">{{ r.domain }}</span>
                    <span class="rounded bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600">{{ _mft('setting:domains.buy.taken') }}</span>
                </li>
            </ul>
        </div>

        <div
            v-if="hasSearched && !isSearching && !results.length"
            class="rounded-lg border bg-white p-6 text-center text-sm text-gray-500 shadow-sm"
        >
            {{ _mft('setting:domains.buy.noMatches') }}
        </div>
    </div>
</template>

<style scoped>
.result-enter-active,
.result-leave-active {
    transition: all 0.25s ease;
}
.result-enter-from {
    opacity: 0;
    transform: translateY(4px);
}
.result-leave-to {
    opacity: 0;
}
</style>
